import React from 'react';
import {
  Box,
  Chip,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Tooltip,
  Avatar,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  TrendingUp as TrendingUpIcon,
  Storefront as StorefrontIcon,
} from '@mui/icons-material';
import { Product, Category, Warehouse, calculateDiscountedPrice } from '../../../enums/product';
import { Brand } from '../brand/brandService';
import { useCompany } from '../../../contexts/CompanyContext';

interface ProductTableProps {
  products: Product[];
  categories: Category[];
  warehouses: Warehouse[];
  brands: Brand[];
  onView: (product: Product) => void;
  onEdit: (product: Product) => void;
  onDelete: (product: Product) => void;
  onToggleFeatured: (product: Product) => void;
  deleteLoading: string;
  featuredLoading: string;
  // ── NEW: passed from ProductsPage so chips use the correct palette in both
  // light and dark mode. Without this, all chips use hardcoded light-mode
  // colours and become nearly invisible on dark paper backgrounds.
  isDark?: boolean;
  canEdit?: boolean;           // ADD
  canDelete?: boolean;         // ADD
  canToggleFeatured?: boolean; // ADD
}

// ── Theme-aware chip style helper ─────────────────────────────────────────────
//
// In light mode: soft pastel tints (low-opacity fills).
// In dark mode:  richer semi-transparent fills + brighter text so the chip
//                is legible against dark paper backgrounds.
//
// Pass `isDark` (from useTheme in the parent) to switch between palettes.
// ─────────────────────────────────────────────────────────────────────────────
const getSoftChipSx = (color: string, isDark: boolean = false) => {
  type PaletteEntry = { bg: string; border: string; text: string; iconColor: string };

  const lightPalette: Record<string, PaletteEntry> = {
    warning: { bg: 'rgba(255,167,38,0.12)', border: 'rgba(255,167,38,0.4)', text: '#b76e00', iconColor: '#e68900' },
    info: { bg: 'rgba(41,182,246,0.12)', border: 'rgba(41,182,246,0.4)', text: '#0277bd', iconColor: '#039be5' },
    primary: { bg: 'rgba(33,150,243,0.12)', border: 'rgba(33,150,243,0.4)', text: '#1565c0', iconColor: '#1976d2' },
    success: { bg: 'rgba(102,187,106,0.15)', border: 'rgba(102,187,106,0.45)', text: '#2e7d32', iconColor: '#43a047' },
    error: { bg: 'rgba(239,83,80,0.12)', border: 'rgba(239,83,80,0.4)', text: '#c62828', iconColor: '#e53935' },
    secondary: { bg: 'rgba(171,71,188,0.10)', border: 'rgba(171,71,188,0.35)', text: '#6a1b9a', iconColor: '#8e24aa' },
    default: { bg: 'rgba(158,158,158,0.12)', border: 'rgba(158,158,158,0.4)', text: '#424242', iconColor: '#757575' },
  };

  // Dark mode: higher-opacity fills + light/vivid text so chips pop on dark surfaces
  const darkPalette: Record<string, PaletteEntry> = {
    warning: { bg: 'rgba(255,167,38,0.22)', border: 'rgba(255,167,38,0.55)', text: '#ffcc80', iconColor: '#ffa726' },
    info: { bg: 'rgba(41,182,246,0.20)', border: 'rgba(41,182,246,0.50)', text: '#81d4fa', iconColor: '#29b6f6' },
    primary: { bg: 'rgba(33,150,243,0.22)', border: 'rgba(33,150,243,0.50)', text: '#90caf9', iconColor: '#42a5f5' },
    success: { bg: 'rgba(102,187,106,0.22)', border: 'rgba(102,187,106,0.50)', text: '#a5d6a7', iconColor: '#66bb6a' },
    error: { bg: 'rgba(239,83,80,0.22)', border: 'rgba(239,83,80,0.50)', text: '#ef9a9a', iconColor: '#ef5350' },
    secondary: { bg: 'rgba(171,71,188,0.22)', border: 'rgba(171,71,188,0.50)', text: '#ce93d8', iconColor: '#ab47bc' },
    default: { bg: 'rgba(158,158,158,0.22)', border: 'rgba(158,158,158,0.50)', text: '#e0e0e0', iconColor: '#bdbdbd' },
  };

  const palette = isDark ? darkPalette : lightPalette;
  const p = palette[color] ?? palette.default;

  return {
    backgroundColor: p.bg,
    borderColor: p.border,
    color: p.text,
    fontWeight: 600,
    border: `1px solid ${p.border}`,
    '& .MuiChip-icon': { color: p.iconColor },
    '& .MuiChip-label': { color: p.text },
  };
};

const ProductTable: React.FC<ProductTableProps> = ({
  products,
  categories,
  warehouses,
  brands,
  onView,
  onEdit,
  onDelete,
  onToggleFeatured,
  deleteLoading,
  featuredLoading,
  isDark = false,
  canEdit = false,           // ADD
  canDelete = false,         // ADD
  canToggleFeatured = false, // ADD
}) => {
  const { companyProfile } = useCompany();

  const companyCurrency = companyProfile?.currency || 'LKR';

  const getCurrencySymbol = (currency: string) => {
    const symbols: { [key: string]: string } = {
      USD: '$', EUR: '€', GBP: '£', JPY: '¥', CNY: '¥',
      INR: '₹', LKR: 'Rs.', AUD: 'A$', CAD: 'C$',
    };
    return symbols[currency] || currency;
  };

  const currencySymbol = getCurrencySymbol(companyCurrency);

  const formatPrice = (price: number) =>
    `${currencySymbol} ${price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const formatDate = (date: Date | undefined) => {
    if (!date) return '-';
    return new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'short', day: 'numeric' }).format(date);
  };

  const getCategoryName = (categoryId: string): string => {
    const category = categories.find((cat) => cat.id === categoryId);
    return category ? category.name : categoryId;
  };

  const getBrand = (brandId?: string | null): Brand | undefined => {
    if (!brandId) return undefined;
    return brands.find(b => b.id === brandId);
  };

  return (
    <TableContainer component={Paper} sx={{ overflowX: 'auto', height: '100%' }}>
      <Table stickyHeader sx={{ minWidth: 1200 }}>
        <TableHead>
          <TableRow>
            {canToggleFeatured && <TableCell width="48px" />}
            <TableCell>Product</TableCell>
            <TableCell>SKU</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Category</TableCell>
            <TableCell>Brand</TableCell>
            <TableCell>Price</TableCell>
            <TableCell>Stock</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Created</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {products.map((product) => {
            const brand = getBrand(product.brandId);
            return (
              <TableRow
                key={product.id}
                hover
                sx={{
                  '&:hover': { backgroundColor: 'action.hover' },
                  '&:last-child td, &:last-child th': { borderBottom: 0 },
                }}
              >
                {/* Featured toggle */}
                {canToggleFeatured && (
                  <TableCell>
                    <Tooltip title={product.isFeatured ? 'Remove from featured' : 'Mark as featured'}>
                      <IconButton
                        onClick={() => onToggleFeatured(product)}
                        disabled={featuredLoading === product.id}
                        size="small"
                        sx={{
                          color: product.isFeatured ? '#ff9800' : 'inherit',
                          '&:hover': { color: product.isFeatured ? '#f57c00' : '#ff9800' },
                        }}
                      >
                        {featuredLoading === product.id ? (
                          <CircularProgress size={16} />
                        ) : product.isFeatured ? (
                          <StarIcon fontSize="small" />
                        ) : (
                          <StarBorderIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                )}

                {/* Product name + image */}
                <TableCell>
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Avatar
                      src={product.images.length > 0 ? product.images[0] : undefined}
                      variant="rounded"
                      sx={{
                        width: 44,
                        height: 44,
                        bgcolor: product.images.length > 0 ? 'transparent' : 'grey.200',
                        flexShrink: 0,
                      }}
                      onError={(e) => {
                        (e.target as HTMLImageElement).src =
                          'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDgiIGhlaWdodD0iNDgiIHZpZXdCb3g9IjAgMCA0OCA0OCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjQ4IiBoZWlnaHQ9IjQ4IiByeD0iNiIgZmlsbD0iI0Y5RkFGQiIvPgo8cGF0aCBkPSJNMjQgMjBDMjYuMjA5MSAyMCAyOCAxOC4yMDkxIDI4IDE2QzI4IDEzLjc5MDkgMjYuMjA5MSAxMiAyNCAxMkMyMS43OTA5IDEyIDIwIDEzLjc5MDkgMjAgMTZDMjAgMTguMjA5MSAyMS43OTA5IDIwIDI0IDIwWiIgZmlsbD0iI0Q1RDlERCIvPgo8cGF0aCBkPSJNMzYgMzZIMTJMMjAgMjhMMjQgMzJMMzIgMjRMMzYgMzZaIiBmaWxsPSIjRDVEOUREIi8+Cjwvc3ZnPgo=';
                      }}
                    >
                      {product.images.length === 0 && <TrendingUpIcon />}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Typography
                          variant="subtitle2"
                          noWrap
                          sx={{ fontWeight: 600, maxWidth: 180 }}
                        >
                          {product.name}
                        </Typography>
                        {product.isFeatured && (
                          <Chip
                            label="Featured"
                            size="small"
                            variant="outlined"
                            sx={{
                              height: 18,
                              fontSize: '0.65rem',
                              ...getSoftChipSx('warning', isDark),
                            }}
                          />
                        )}
                      </Stack>
                    </Box>
                  </Stack>
                </TableCell>

                {/* SKU */}
                <TableCell>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {product.sku || (product.hasVariants ? 'N/A' : '-')}
                  </Typography>
                </TableCell>

                {/* Type — fully uses getSoftChipSx so it respects dark mode
                    correctly. The previous inline theme.palette.primary hex
                    interpolation rendered as the app's teal primary in dark
                    mode, making the chip background too vivid to read. */}
                <TableCell>
                  <Chip
                    label={product.hasVariants ? 'With Variants' : 'Simple'}
                    size="small"
                    variant="outlined"
                    sx={getSoftChipSx(product.hasVariants ? 'primary' : 'default', isDark)}
                  />
                </TableCell>

                {/* Category — same fix: replaced inline theme colour
                    interpolation with getSoftChipSx('info', isDark) so the
                    chip is clearly legible in both light and dark mode. */}
                <TableCell>
                  <Chip
                    label={getCategoryName(product.categoryId)}
                    size="small"
                    variant="outlined"
                    sx={getSoftChipSx('info', isDark)}
                  />
                </TableCell>

                {/* Brand */}
                <TableCell>
                  {brand ? (
                    <Tooltip title={brand.websiteUrl || brand.name}>
                      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ cursor: 'default' }}>
                        {brand.imageUrl ? (
                          <Avatar
                            src={brand.imageUrl}
                            alt={brand.name}
                            variant="rounded"
                            sx={{ width: 20, height: 20, flexShrink: 0 }}
                          />
                        ) : (
                          <Avatar variant="rounded" sx={{ width: 20, height: 20, bgcolor: 'grey.200', flexShrink: 0 }}>
                            <StorefrontIcon sx={{ fontSize: 12, color: 'grey.500' }} />
                          </Avatar>
                        )}
                        <Typography variant="body2" noWrap sx={{ maxWidth: 100 }}>
                          {brand.name}
                        </Typography>
                      </Stack>
                    </Tooltip>
                  ) : (
                    <Typography variant="body2" color="text.disabled" sx={{ fontSize: '0.75rem' }}>
                      —
                    </Typography>
                  )}
                </TableCell>

                {/* Price — isDark passed so discount chip uses dark-mode palette */}
                <TableCell>
                  {product.hasVariants ? (
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>Varies</Typography>
                  ) : (
                    <Stack direction="column" spacing={0.5}>
                      {(product.discount || 0) > 0 ? (
                        <>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <Typography
                              variant="body2"
                              sx={{ textDecoration: 'line-through', color: 'text.secondary', fontSize: '0.75rem' }}
                            >
                              {formatPrice(product.price)}
                            </Typography>
                            <Chip
                              label={`${product.discount}%`}
                              size="small"
                              variant="outlined"
                              sx={{
                                height: 16,
                                fontSize: '0.65rem',
                                ...getSoftChipSx('success', isDark),
                              }}
                            />
                          </Stack>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                            {formatPrice(calculateDiscountedPrice(product.price, product.discount))}
                          </Typography>
                        </>
                      ) : (
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {formatPrice(product.price)}
                        </Typography>
                      )}
                    </Stack>
                  )}
                </TableCell>

                {/* Stock — isDark passed so Low Stock chip uses dark-mode palette */}
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {product.stockQuantity.toLocaleString()} units
                  </Typography>
                  {product.isLowStock && !product.isOutOfStock && (
                    <Chip
                      label="Low Stock"
                      size="small"
                      variant="outlined"
                      sx={{
                        mt: 0.5,
                        ...getSoftChipSx('warning', isDark),
                      }}
                    />
                  )}
                </TableCell>

                {/* Status — isDark passed so chip uses dark-mode palette */}
                <TableCell>
                  <Chip
                    label={product.isOutOfStock ? 'Out of Stock' : 'In Stock'}
                    size="small"
                    variant="outlined"
                    icon={product.isOutOfStock ? <WarningIcon /> : undefined}
                    sx={getSoftChipSx(product.isOutOfStock ? 'error' : 'success', isDark)}
                  />
                </TableCell>

                {/* Created */}
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {formatDate(product.createdAt)}
                  </Typography>
                </TableCell>

                {/* Actions */}
                <TableCell align="right">
                  <Stack direction="row" spacing={0.5} justifyContent="flex-end" flexWrap="nowrap">
                    <Tooltip title="View Product">
                      <IconButton onClick={() => onView(product)} size="small" color="primary">
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {canEdit && (
                      <Tooltip title="Edit Product">
                        <IconButton onClick={() => onEdit(product)} size="small" color="warning">
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canDelete && (
                      <Tooltip title="Delete Product">
                        <IconButton
                          onClick={() => onDelete(product)}
                          disabled={deleteLoading === product.id}
                          size="small"
                          color="error"
                        >
                          {deleteLoading === product.id ? (
                            <CircularProgress size={16} />
                          ) : (
                            <DeleteIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ProductTable;